import React, { useState, useMemo, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import { getConsultationReport } from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { normalizeAdminListResponse } from '../../utils/adminListResponse';
import { useAdminListView } from '../../hooks/useAdminListView';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import BrandBranchFilterFields from '../../components/BrandBranchFilterFields';
import usePortalScope from '../../hooks/usePortalScope';
import { tenantListParams } from '../../utils/contentScope';
import './AdminPages.css';

const COLUMNS = [
  { key: 'sno', label: 'SNO', width: '50px' },
  { key: 'date', label: 'Date' },
  { key: 'brand', label: 'Brand' },
  { key: 'branch', label: 'Branch' },
  { key: 'mirror', label: 'Mirror' },
  { key: 'category', label: 'Category' },
  { key: 'service', label: 'Service' },
  { key: 'stylist', label: 'Stylist' },
  { key: 'customerName', label: 'Customer Name' },
  { key: 'customerMobile', label: 'Mobile' },
  { key: 'gender', label: 'Gender' },
  { key: 'hairType', label: 'Hair Type' },
  { key: 'billValue', label: 'Bill Value' },
  { key: 'avgServiceTime', label: 'Avg Service Time' },
  { key: 'avgConsultingTime', label: 'Avg Consulting Time' },
  { key: 'stylistFeedback', label: 'Stylist Feedback' },
  { key: 'serviceFeedback', label: 'Service Feedback' },
  { key: 'amountPaidOnQR', label: 'Amount Paid on QR' },
  { key: 'transactionId', label: 'Transaction ID' },
  { key: 'txnRefNo', label: 'Txn Ref No' }
];

const ConsultationReport = () => {
  const portal = usePortalScope();
  const salons = portal.salons;
  const brands = portal.brands;
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    brandId: '',
    salonId: '',
    branch: '',
    mobile: ''
  });

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const getReportParams = useCallback(() => {
    const params = {};
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    Object.assign(params, tenantListParams(portal, filters));
    if (filters.branch) params.branch = filters.branch;
    if (filters.mobile) params.mobile = filters.mobile;
    return params;
  }, [filters, portal]);

  const { items: reportData, loading, loadingMore, pagination, loadMore } = useAdminInfiniteList({
    load: getConsultationReport,
    resetKey: filtersKey,
    getParams: getReportParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const getReportRowId = useCallback(
    (row) => `${row.sno}-${row.transactionId || ''}-${row.customerMobile || ''}`,
    []
  );

  const listView = useAdminListView(reportData, getReportRowId, {
    defaultView: 'table',
    resetKey: filtersKey
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatReportCell = (row, key) => {
    if (key === 'date') return formatDate(row.consultationAt);
    if (key === 'billValue' || key === 'amountPaidOnQR') {
      return row[key] !== '-' ? `₹${Number(row[key]).toLocaleString()}` : row[key];
    }
    return row[key];
  };

  const reportColumns = useMemo(
    () =>
      COLUMNS.map((col) => ({
        key: col.key,
        label: col.label,
        render: (row) => formatReportCell(row, col.key)
      })),
    []
  );

  const reportListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && reportData.length > 0 && (
        <p className="admin-infinite-end">All rows loaded ({pagination.total} total).</p>
      )}
    </>
  );

  const reportEmptyState = (
    <div className="admin-card empty-state">
      <p>No data found.</p>
    </div>
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ dateFrom: '', dateTo: '', brandId: '', salonId: '', branch: '', mobile: '' });
  };

  const exportToCSV = async () => {
    setExporting(true);
    try {
      const params = { ...getReportParams(), all: true };
      const response = await getConsultationReport(params);
      const { data: rows } = normalizeAdminListResponse(response);
      const headers = COLUMNS.map((c) => c.label).join(',');
      const body = rows.map((row) =>
        COLUMNS.map((c) => {
          const val = formatReportCell(row, c.key);
          const str = val === null || val === undefined ? '' : String(val);
          return str.includes(',') || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
        }).join(',')
      );
      const csv = [headers, ...body].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `consultation-report-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setExporting(false);
    }
  };

  const branches = [...new Set(salons.map((s) => s.branch).filter(Boolean))];

  return (
    <AdminLayout
      pageTitle="Consultation Report"
      subtitle={
        pagination.total
          ? `${reportData.length} of ${pagination.total} rows · scroll for more · export loads full result set`
          : 'Generate and export consultation data'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header">
          <h1>Consultation Report</h1>
          <button
            className="btn btn-primary"
            onClick={exportToCSV}
            disabled={exporting || loading}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>

        <div className="admin-card filter-card">
          <h3>Filters</h3>
          <div className="filter-grid">
            <div className="form-group">
              <label>Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
            <BrandBranchFilterFields
              portal={portal}
              filters={filters}
              onChange={handleFilterChange}
              brands={brands}
              salons={salons}
              salonLabel="Salon"
              allSalonLabel="All Salons"
            />
            <div className="form-group">
              <label>Branch</label>
              <input
                type="text"
                value={filters.branch}
                onChange={(e) => handleFilterChange('branch', e.target.value)}
                placeholder="Filter by branch"
                list="report-branch-list"
              />
              <datalist id="report-branch-list">
                {branches.map((b) => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            </div>
            <div className="form-group">
              <label>Mobile</label>
              <input
                type="text"
                value={filters.mobile}
                onChange={(e) => handleFilterChange('mobile', e.target.value)}
                placeholder="Search by mobile"
              />
            </div>
            <div className="form-group filter-actions">
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <Loader variant="dark" fullScreen={false} />
        ) : (
          <>
            <AdminListViewControls
              viewMode={listView.viewMode}
              onViewModeChange={listView.setViewMode}
              selectedCount={listView.selectedCount}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              totalCount={reportData.length}
            showSelection={false}
            />
            <AdminSelectableList
              items={reportData}
              getItemId={getReportRowId}
              viewMode={listView.viewMode}
            selectable={false}
              isSelected={listView.isSelected}
              onToggleItem={listView.toggleItem}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              columns={reportColumns}
              tableClassName="report-table"
              renderGridCard={(row) => (
                <div className="admin-card admin-card--stretch transaction-card">
                  <div className="transaction-header">
                    <div>
                      <h3>{row.customerName || 'Unknown customer'}</h3>
                      <p className="transaction-date">{formatReportCell(row, 'date')}</p>
                    </div>
                    <span className="status-badge active">#{row.sno}</span>
                  </div>
                  <div className="transaction-details">
                    <p><strong>Brand:</strong> {row.brand || '-'}</p>
                    <p><strong>Branch:</strong> {row.branch || '-'}</p>
                    <p><strong>Service:</strong> {row.service || '-'}</p>
                    <p><strong>Stylist:</strong> {row.stylist || '-'}</p>
                    <p><strong>Mobile:</strong> {row.customerMobile || '-'}</p>
                    <p><strong>Bill Value:</strong> {formatReportCell(row, 'billValue')}</p>
                    {row.transactionId && row.transactionId !== '-' && (
                      <p><strong>Transaction ID:</strong> {row.transactionId}</p>
                    )}
                  </div>
                </div>
              )}
              emptyState={reportEmptyState}
              scrollRef={scrollRef}
              footer={reportListFooter}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ConsultationReport;
